/**
 * AK-938 — черновик формы «Схема опыта» и правки копии при закладке опыта.
 *
 * Чистый слой (без React): разбор дозы по правилу «пусто ≠ мусор»
 * (frontend-vite/CLAUDE.md), проверка ролей вариантов (контроль ровно один,
 * эталон 0..1, опытных 1..n — ADR-086 D6) и сборка тела запроса. Ошибки —
 * словами агронома, до отправки, а не 422 с сервера.
 */
import type {
  CheckValueKind,
  ProductKind,
  SchemeVariantBody,
  TrialCheckItem,
  TrialFactor,
  TrialInput,
  TrialScheme,
  TrialSchemeBody,
  VariantRole,
} from './types';

export interface InputDraft {
  key: string;
  product_kind: ProductKind;
  product_id: string;
  product_name: string;
  /** Набранное человеком — как есть (с запятой, пробелами). */
  rateText: string;
  rate_unit_id: string | null;
  rate_unit_symbol: string | null;
  timing_note: string;
}

export interface VariantDraft {
  key: string;
  /** id варианта схемы — нужен при закладке опыта (копия по варианту). */
  schemeVariantId?: string;
  name: string;
  role: VariantRole;
  note: string;
  inputs: InputDraft[];
}

export interface CheckDraft {
  key: string;
  label: string;
  value_kind: CheckValueKind;
  choicesText: string;
}

export interface SchemeDraft {
  name: string;
  seasonYear: number;
  comment: string;
  factors: TrialFactor[];
  variants: VariantDraft[];
  checkItems: CheckDraft[];
}

let seq = 0;
export function draftKey(prefix = 'k'): string {
  seq += 1;
  return `${prefix}${seq}`;
}

export function emptyVariant(role: VariantRole, name: string): VariantDraft {
  return { key: draftKey('v'), name, role, note: '', inputs: [] };
}

export function emptySchemeDraft(seasonYear: number): SchemeDraft {
  return {
    name: '',
    seasonYear,
    comment: '',
    factors: ['crop_protection'],
    variants: [emptyVariant('control', 'Контроль'), emptyVariant('test', 'Вариант 1')],
    checkItems: [],
  };
}

export function inputToDraft(i: TrialInput): InputDraft {
  return {
    key: draftKey('i'),
    product_kind: i.product_kind,
    product_id: i.product_id,
    product_name: i.product_name ?? '',
    rateText: i.rate != null ? String(i.rate).replace('.', ',') : '',
    rate_unit_id: i.rate_unit_id ?? null,
    rate_unit_symbol: i.rate_unit_symbol ?? null,
    timing_note: i.timing_note ?? '',
  };
}

export function schemeToDraft(s: TrialScheme): SchemeDraft {
  return {
    name: s.name,
    seasonYear: s.season_year,
    comment: s.comment ?? '',
    factors: [...s.factors],
    variants: s.variants.map((v) => ({
      key: draftKey('v'),
      schemeVariantId: v.id,
      name: v.name,
      role: v.role,
      note: v.note ?? '',
      inputs: v.inputs.map(inputToDraft),
    })),
    checkItems: s.check_items.map((c) => ({
      key: draftKey('c'),
      label: c.label,
      value_kind: c.value_kind,
      choicesText: (c.choices ?? []).join(', '),
    })),
  };
}

export type RateParse = { ok: true; value: number | null } | { ok: false; error: string };

/** Доза: пусто → null (осознанно), число → значение, иначе — ошибка с примером. */
export function parseRate(text: string): RateParse {
  const t = text.trim().replace(/\s+/g, '').replace(',', '.');
  if (t === '') return { ok: true, value: null };
  if (!/^\d+(\.\d+)?$/.test(t)) {
    return { ok: false, error: 'Нужно число, например 0,8 или 20' };
  }
  const v = Number(t);
  if (!(v > 0)) return { ok: false, error: 'Доза больше нуля, например 0,8' };
  return { ok: true, value: v };
}

/** Ошибки по ключу строки: `name`, `factors`, `roles`, `<inputKey>`, `<checkKey>`. */
export type DraftErrors = Record<string, string>;

/** Ошибки ролей (для активной схемы): контроль ровно один, эталон 0..1, опытных ≥1. */
export function roleErrors(roles: VariantRole[]): string | null {
  const control = roles.filter((r) => r === 'control').length;
  const reference = roles.filter((r) => r === 'reference').length;
  const test = roles.filter((r) => r === 'test').length;
  if (control !== 1) return 'Контроль — ровно один вариант';
  if (reference > 1) return 'Эталон — не больше одного';
  if (test < 1) return 'Нужен хотя бы один опытный вариант';
  return null;
}

/** Разобрать препараты варианта; ошибки складываются в `errors` по ключу строки. */
export function buildInputs(inputs: InputDraft[], errors: DraftErrors): TrialInput[] {
  const out: TrialInput[] = [];
  for (const i of inputs) {
    if (!i.product_id) {
      errors[i.key] = 'Выберите препарат из справочника';
      continue;
    }
    const rate = parseRate(i.rateText);
    if (!rate.ok) {
      errors[i.key] = rate.error;
      continue;
    }
    if (rate.value != null && !i.rate_unit_id) {
      errors[i.key] = 'Укажите единицу дозы, например л/га';
      continue;
    }
    if (rate.value == null && !i.timing_note.trim()) {
      errors[i.key] = 'Укажите дозу или срок применения';
      continue;
    }
    out.push({
      product_kind: i.product_kind,
      product_id: i.product_id,
      rate: rate.value,
      rate_unit_id: rate.value != null ? i.rate_unit_id : null,
      rate_basis: rate.value != null ? 'per_ha' : null,
      timing_note: i.timing_note.trim() || null,
    });
  }
  return out;
}

export function buildCheckItems(items: CheckDraft[], errors: DraftErrors): TrialCheckItem[] {
  const out: TrialCheckItem[] = [];
  for (const c of items) {
    if (!c.label.trim()) {
      errors[c.key] = 'Что смотрим — например «Засорённость»';
      continue;
    }
    const choices = c.choicesText
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
    if (c.value_kind === 'choice' && !choices.length) {
      errors[c.key] = 'Перечислите ответы через запятую, например «нет, слабо, сильно»';
      continue;
    }
    out.push({
      label: c.label.trim(),
      value_kind: c.value_kind,
      choices: c.value_kind === 'choice' ? choices : null,
    });
  }
  return out;
}

export type SchemeBuild =
  | { ok: true; body: TrialSchemeBody }
  | { ok: false; errors: DraftErrors };

/**
 * Тело схемы. `active` — схема готова к закладке (роли проверяются);
 * черновик сохраняется и с неполным составом вариантов.
 */
export function buildSchemeBody(d: SchemeDraft, status: 'draft' | 'active'): SchemeBuild {
  const errors: DraftErrors = {};
  if (!d.name.trim()) errors.name = 'Назовите схему, например «Гербицид против двудольных в пшенице»';
  if (!d.factors.length) errors.factors = 'Отметьте хотя бы один фактор';
  const variants: SchemeVariantBody[] = d.variants.map((v, idx) => {
    if (!v.name.trim()) errors[v.key] = `Назовите вариант ${idx + 1}`;
    return {
      name: v.name.trim(),
      role: v.role,
      note: v.note.trim() || null,
      inputs: buildInputs(v.inputs, errors),
    };
  });
  if (status === 'active') {
    const r = roleErrors(d.variants.map((v) => v.role));
    if (r) errors.roles = r;
  }
  const check_items = buildCheckItems(d.checkItems, errors);
  if (Object.keys(errors).length) return { ok: false, errors };
  return {
    ok: true,
    body: {
      name: d.name.trim(),
      season_year: d.seasonYear,
      comment: d.comment.trim() || null,
      factors: d.factors,
      status,
      variants,
      check_items,
    },
  };
}
